import { cn } from "@utils/tw"
import { ComponentPropsWithoutRef, forwardRef } from "react"

export const MobileHeader = forwardRef<HTMLElement, ComponentPropsWithoutRef<"header">>(
  ({ className, ...props }, ref) => (
    <header
      ref={ref}
      className={cn("w-full flex flex-col rounded-md overflow-hidden border border-grey my-7", className)}
      {...props}
    />
  ),
)
MobileHeader.displayName = "MobileHeader"

export const MetaBox = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "w-full flex flex-col rounded-md overflow-hidden p-5 bg-light-grey mb-8 [&_.image-wrapper]:rounded-[6px]",
      className,
    )}
    {...props}
  />
))
MetaBox.displayName = "MetaBox"

export const ContentContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("w-full flex flex-col p-6 [&_h1_span]:font-normal", className)} {...props} />
  ),
)
ContentContainer.displayName = "ContentContainer"

export const Buttons = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("w-full flex flex-col gap-3 mb-7 [&_button_span]:grow [&_button_span]:text-left", className)}
    {...props}
  />
))
Buttons.displayName = "Buttons"

export const Meta = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("w-full flex flex-col gap-5", className)} {...props} />
))
Meta.displayName = "Meta"

export const MetaValue = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("w-full flex flex-col gap-1", className)} {...props} />
))
MetaValue.displayName = "MetaValue"

export const PageContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex flex-col mb-8 [&_h1_span]:font-normal",
        // Original stitches rule used `typography: "body-m"`, a key that
        // was never registered in theme.css.tsx's typography map (only
        // text-l/m/s/xs existed pre-migration) — the util silently resolved
        // to nothing, so only `pb: 0` ever actually applied here. Preserved
        // as-is (no type-* class) to match existing (buggy) behavior.
        "[&_.blocks>div>p]:pb-0",
        "[&_.blocks>div:first-of-type>p]:type-title-s [&_.blocks>div:first-of-type>p]:p-0 [&_.blocks>div:first-of-type>p]:my-5",
        className,
      )}
      {...props}
    />
  ),
)
PageContent.displayName = "PageContent"

export const Chips = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-wrap gap-3 absolute top-4 left-4 !shadow-none md:relative md:top-auto md:left-auto",
      className,
    )}
    {...props}
  />
))
Chips.displayName = "Chips"

export const ImageContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "relative bg-light-grey p-[55px_84px_0_84px] overflow-hidden",
        // `calc((100vw - (100vw - $grid$inner$width)) * (194 / 344))` in the
        // original — the two 100vw terms cancel out algebraically, leaving
        // just `$grid$inner$width * (194/344)`, now `--tw-grid-inner-width`.
        "max-h-[calc(var(--tw-grid-inner-width)*(194/344))]",
        "[&>div]:rounded-[6px] [&>div]:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]",
        className,
      )}
      {...props}
    />
  ),
)
ImageContainer.displayName = "ImageContainer"

export const LogoContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "w-[64px] h-[64px] flex items-center [&>div]:bg-transparent [&>div]:w-full [&>div]:h-full",
        className,
      )}
      {...props}
    />
  ),
)
LogoContainer.displayName = "LogoContainer"
